import { DateTime } from 'luxon';
import { MANUAL_REVIEW_SERVICE_DATE } from '@billing/validation';

/*********** Types and Options ***********/

/** Values accepted by the shared Luxon parser. */
export type DateTimeInput = string | DateTime | Date | null | undefined;

/** Options passed to Luxon's ISO/Date parser. */
export type ParseDateTimeOptions = {
  zone?: string | null;
  setZone?: boolean;
};

/*********** Shared Parsing Constants and Internals ***********/

export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const DEFAULT_SITE_TIME_ZONE = 'local';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const resolveDateTimeZone = (timezone?: string | null) =>
  timezone || DEFAULT_SITE_TIME_ZONE;

export const invalidDateTime = () => DateTime.invalid('invalid input');

export const nowDateTime = (timezone?: string | null) =>
  DateTime.now().setZone(resolveDateTimeZone(timezone));

export const isFutureDate = (
  value: DateTimeInput,
  timezone?: string | null
): boolean => {
  const date = parseDateTime(value, { zone: timezone });

  return (
    date.isValid && date.startOf('day') > nowDateTime(timezone).startOf('day')
  );
};

export const toISODateOrEmpty = (value: DateTime) => value.toISODate() || '';

export const emptyDateRange = (): [string, string] => ['', ''];

export const toISODateRange = (
  start: DateTime,
  end: DateTime
): [string, string] => [toISODateOrEmpty(start), toISODateOrEmpty(end)];

export const toUtcISO = (value: DateTime) => value.toUTC().toISO();

/**
 * Detects existing API/form calendar date tokens. Use before parsing when a
 * `YYYY-MM-DD` string should stay a calendar date instead of becoming an
 * instant in a timezone.
 */
export const isDateOnlyString = (value: unknown): value is string =>
  typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim());

/**
 * Parses shared app date inputs into a Luxon DateTime. Use this when code needs
 * date math, zone conversion, comparison details, or validity checks.
 *
 * Invalid, empty, or unsupported inputs return an invalid DateTime instead of
 * throwing, so callers can branch on `.isValid`.
 */
export const parseDateTime = (
  value: DateTimeInput,
  options: ParseDateTimeOptions = {}
): DateTime => {
  if (value === null || value === undefined || value === '') {
    return invalidDateTime();
  }

  const zone = options.zone || undefined;

  if (DateTime.isDateTime(value)) {
    return zone ? value.setZone(zone) : value;
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value, zone ? { zone } : undefined);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return invalidDateTime();

    return DateTime.fromISO(trimmed, {
      zone,
      setZone: options.setZone,
    });
  }

  return invalidDateTime();
};

export const parseUnknownDateTime = (
  value: unknown,
  options?: ParseDateTimeOptions
): DateTime => {
  if (DateTime.isDateTime(value)) {
    return value.isValid ? parseDateTime(value, options) : invalidDateTime();
  }

  if (value instanceof Date || typeof value === 'string') {
    return parseDateTime(value, options);
  }

  return invalidDateTime();
};

export const parseInZone = (value: DateTimeInput, timezone?: string | null) =>
  parseDateTime(value, { zone: resolveDateTimeZone(timezone) });

export const isManualReviewServiceStart = (
  serviceStart: string | null | undefined,
  timezone: string
): boolean => {
  const serviceDate = parseDateTime(serviceStart, { setZone: true });

  return (
    serviceDate.isValid &&
    serviceDate.setZone(timezone).toISODate() === MANUAL_REVIEW_SERVICE_DATE
  );
};

export const shouldDefaultBulkEditServiceTimes = (
  serviceStarts: Array<string | null | undefined>,
  timezone: string
): boolean =>
  serviceStarts.length > 0 &&
  serviceStarts.every((serviceStart) =>
    isManualReviewServiceStart(serviceStart, timezone)
  );

const mapValidDateTime = <T>(
  value: DateTimeInput,
  mapper: (value: DateTime) => T,
  options?: ParseDateTimeOptions
) => {
  const parsed = parseDateTime(value, options);
  return parsed.isValid ? mapper(parsed) : null;
};

/**
 * Validation guard for values accepted by `parseDateTime`.
 */
export const isValidDateValue = (
  value: DateTimeInput,
  options?: ParseDateTimeOptions
) => parseDateTime(value, options).isValid;

/**
 * Converts to native Date for component/library APIs that do not accept Luxon.
 * Keep this at boundaries such as Mantine or table filter props.
 */
export const toJSDate = (
  value: DateTimeInput,
  options?: ParseDateTimeOptions
): Date | null =>
  mapValidDateTime(value, (parsed) => parsed.toJSDate(), options);

/**
 * Converts to a date-only ISO token (`YYYY-MM-DD`) for date-only API fields,
 * form state, and query values.
 */
export const toISODate = (
  value: DateTimeInput,
  options?: ParseDateTimeOptions
): string | null =>
  mapValidDateTime(value, (parsed) => parsed.toISODate(), options);

/**
 * Converts to a full ISO timestamp for backend/API fields that represent an
 * instant rather than a calendar date.
 */
export const toISODateTime = (
  value: DateTimeInput,
  options?: ParseDateTimeOptions
): string | null =>
  mapValidDateTime(value, (parsed) => parsed.toISO(), options);

/**
 * Returns the current timestamp in the site/default timezone as an ISO string.
 * Use for timestamp fields such as `createdAt`, `updatedAt`, and draft saves.
 */
export const nowISO = (timezone?: string | null) =>
  nowDateTime(timezone).toISO();

/**
 * Returns today's calendar date in the site/default timezone. Use for
 * date-only form defaults and max/min date values.
 */
export const nowISODate = (timezone?: string | null) =>
  nowDateTime(timezone).toISODate();

/**
 * Parses a 24-hour time input (`HH:mm` or `H:mm`). Use when a time-only control
 * needs hour/minute values that will later be applied to a real date.
 */
export const parseTimeInput = (value: string) => {
  const trimmed = value.trim();

  for (const format of ['HH:mm', 'H:mm'] as const) {
    const parsed = DateTime.fromFormat(trimmed, format);
    if (parsed.isValid) return parsed;
  }

  return invalidDateTime();
};

/**
 * Converts a date value to epoch milliseconds. Use inside comparator or
 * "latest/earliest" logic where invalid values should sort to a known fallback.
 */
export const toDateMillis = (
  value: DateTimeInput,
  options?: ParseDateTimeOptions & { fallback?: number }
) => {
  const parsed = parseDateTime(value, options);
  return parsed.isValid ? parsed.toMillis() : options?.fallback ?? 0;
};

/**
 * Ascending comparator for date values. Use with `Array.sort` to order from
 * oldest to newest.
 */
export const compareDateAsc = (left: DateTimeInput, right: DateTimeInput) =>
  toDateMillis(left) - toDateMillis(right);

/**
 * Descending comparator for date values. Use with `Array.sort` to order from
 * newest to oldest.
 */
export const compareDateDesc = (left: DateTimeInput, right: DateTimeInput) =>
  compareDateAsc(right, left);
